import React, { useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import api from '../../lib/api';
import Button from '../../components/ui/Button';

type PaymentStatus = 'PENDING' | 'PAID' | 'OVERDUE' | 'CANCELLED';

type PaymentAnalyticsResponse = {
  summary: {
    totalPayments: number;
    totalAmount: number;
    pendingCount: number;
    pendingAmount: number;
    paidCount: number;
    paidAmount: number;
  };
  wallets: {
    totalEarned: number;
    totalPaid: number;
    walletCount: number;
  };
  byKOL: Array<{
    kolProfileId: string;
    kolName: string;
    totalAmount: number;
    paymentCount: number;
  }>;
  byJob: Array<{
    jobId: string;
    jobTitle: string;
    totalAmount: number;
    paymentCount: number;
  }>;
  byAgency: Array<{
    agencyOrgId: string | null;
    agencyName: string;
    totalAmount: number;
    paymentCount: number;
  }>;
  overduePayments: Array<{
    id: string;
    amountRM: number;
    job: {
      id: string;
      title: string;
      brandOrg?: { id: string; name: string } | null;
      managedByOrg?: { id: string; name: string } | null;
    };
    kolProfile: { id: string; name: string; email?: string | null };
  }>;
  paymentList: {
    items: PaymentListItem[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  };
};

type PaymentListItem = {
  payment: {
    id: string;
    amountRM: number;
    status: PaymentStatus;
    description?: string | null;
    paidAt?: string | null;
    createdAt: string;
    updatedAt: string;
  };
  job: {
    id: string;
    title: string;
    brandOrgId: string;
    brandOrgName: string;
    managedByOrgId?: string | null;
    managedByOrgName: string;
  };
  kolProfile: {
    id: string;
    name: string;
    email?: string | null;
  };
  invitation: {
    id: string;
    status: string;
    isShortlisted: boolean;
    isApprovedForProduction: boolean;
    createdAt: string;
  } | null;
  kolWork: {
    scripts: { total: number; byStatus: Record<string, number> };
    submissions: { total: number; byStatus: Record<string, number> };
    contracts: { total: number; acknowledged: number; pending: number };
  };
};

const statusOptions: Array<{ label: string; value: '' | PaymentStatus }> = [
  { label: 'All statuses', value: '' },
  { label: 'Pending', value: 'PENDING' },
  { label: 'Paid', value: 'PAID' },
  { label: 'Overdue', value: 'OVERDUE' },
  { label: 'Cancelled', value: 'CANCELLED' },
];

const nativeOptionStyle = { backgroundColor: 'Canvas', color: 'CanvasText' } as const;

const statusBadgeClass: Record<PaymentStatus, string> = {
  PENDING: 'bg-amber-400/15 text-amber-500 dark:text-amber-300 border-amber-400/30',
  PAID: 'bg-emerald-500/15 text-emerald-500 dark:text-emerald-300 border-emerald-500/30',
  OVERDUE: 'bg-rose-500/15 text-rose-500 dark:text-rose-300 border-rose-500/30',
  CANCELLED: 'bg-glass-3 text-glass-text-2 border-glass',
};

function StatusBadge({ status }: { status: PaymentStatus }) {
  return (
    <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-medium ${statusBadgeClass[status]}`}>
      {status}
    </span>
  );
}

function formatStatusMap(map: Record<string, number>) {
  const entries = Object.entries(map);
  if (entries.length === 0) return '0';
  return entries
    .sort((a, b) => b[1] - a[1])
    .map(([k, v]) => `${k}: ${v}`)
    .join(' | ');
}

const PaymentsAnalytics: React.FC = () => {
  const [data, setData] = useState<PaymentAnalyticsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [statusFilter, setStatusFilter] = useState<'' | PaymentStatus>('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await api.get<PaymentAnalyticsResponse>('/analytics/payments', {
          params: {
            status: statusFilter || undefined,
            search: search.trim() || undefined,
            page,
            limit: 20,
          },
        });
        if (!active) return;
        setData(res.data);
      } catch (e: unknown) {
        if (!active) return;
        const err = e as { response?: { data?: { error?: string } } };
        setError(err.response?.data?.error || 'Failed to load analytics');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [statusFilter, search, page]);

  const summary = data?.summary || {
    totalPayments: 0,
    totalAmount: 0,
    pendingCount: 0,
    pendingAmount: 0,
    paidCount: 0,
    paidAmount: 0,
  };
  const wallets = data?.wallets || { totalEarned: 0, totalPaid: 0, walletCount: 0 };
  const byAgency = data?.byAgency || [];
  const byKOL = data?.byKOL || [];
  const byJob = data?.byJob || [];
  const overduePayments = data?.overduePayments || [];
  const paymentItems = data?.paymentList?.items || [];
  const pagination = data?.paymentList?.pagination || { page: 1, limit: 20, total: 0, totalPages: 1 };

  const canPrev = pagination.page > 1;
  const canNext = pagination.page < pagination.totalPages;

  const topKols = useMemo(() => byKOL.slice(0, 5), [byKOL]);
  const topJobs = useMemo(() => byJob.slice(0, 5), [byJob]);

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-[2.25rem] font-semibold tracking-tightest text-glass-text">Payment Analytics</h1>
        <p className="text-glass-text-2 mt-1">Track KOL payments and financial overview.</p>
      </div>

      {error && (
        <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-rose-500 dark:text-rose-300">
          {error}
        </div>
      )}

      <div className="grid md:grid-cols-4 gap-4">
        <div className="glass-card p-6">
          <p className="text-xs text-glass-text-3 uppercase tracking-[0.05em]">Total payments</p>
          <p className="text-3xl font-semibold tracking-tightest text-glass-text mt-1">{summary.totalPayments}</p>
        </div>
        <div className="glass-card p-6">
          <p className="text-xs text-glass-text-3 uppercase tracking-[0.05em]">Total amount</p>
          <p className="text-3xl font-semibold tracking-tightest text-accent2 mt-1">RM {summary.totalAmount.toLocaleString()}</p>
        </div>
        <div className="glass-card p-6">
          <p className="text-xs text-glass-text-3 uppercase tracking-[0.05em]">Pending amount</p>
          <p className="text-3xl font-semibold tracking-tightest text-amber-500 dark:text-amber-300 mt-1">
            RM {summary.pendingAmount.toLocaleString()}
          </p>
          <p className="text-xs text-glass-text-3 mt-1">{summary.pendingCount} payments</p>
        </div>
        <div className="glass-card p-6">
          <p className="text-xs text-glass-text-3 uppercase tracking-[0.05em]">Paid amount</p>
          <p className="text-3xl font-semibold tracking-tightest text-emerald-500 dark:text-emerald-300 mt-1">
            RM {summary.paidAmount.toLocaleString()}
          </p>
          <p className="text-xs text-glass-text-3 mt-1">{summary.paidCount} payments</p>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <div className="glass-card p-5">
          <p className="text-xs text-glass-text-3 uppercase tracking-[0.05em]">Wallet count</p>
          <p className="text-2xl font-semibold tracking-tightest text-glass-text mt-1">{wallets.walletCount}</p>
        </div>
        <div className="glass-card p-5">
          <p className="text-xs text-glass-text-3 uppercase tracking-[0.05em]">Wallet total earned</p>
          <p className="text-2xl font-semibold tracking-tightest text-accent2 mt-1">RM {wallets.totalEarned.toLocaleString()}</p>
        </div>
        <div className="glass-card p-5">
          <p className="text-xs text-glass-text-3 uppercase tracking-[0.05em]">Wallet total paid</p>
          <p className="text-2xl font-semibold tracking-tightest text-emerald-500 dark:text-emerald-300 mt-1">
            RM {wallets.totalPaid.toLocaleString()}
          </p>
        </div>
      </div>

      <div className="grid xl:grid-cols-3 gap-4">
        <div className="glass-card p-5">
          <h3 className="font-semibold text-glass-text mb-3">Overdue (top 5)</h3>
          {overduePayments.length === 0 ? (
            <p className="text-sm text-glass-text-3">No overdue payments.</p>
          ) : (
            <div className="space-y-2">
              {overduePayments.slice(0, 5).map((row) => (
                <div key={row.id} className="rounded-lg border border-rose-500/30 bg-rose-500/10 p-3">
                  <p className="text-sm font-medium text-glass-text">{row.kolProfile.name}</p>
                  <p className="text-xs text-glass-text-3">{row.job.title}</p>
                  <p className="text-sm font-semibold text-rose-500 dark:text-rose-300 mt-1">RM {row.amountRM.toLocaleString()}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="glass-card p-5">
          <h3 className="font-semibold text-glass-text mb-3">Top KOLs</h3>
          {topKols.length === 0 ? (
            <p className="text-sm text-glass-text-3">No data.</p>
          ) : (
            <div className="space-y-2">
              {topKols.map((row) => (
                <div key={row.kolProfileId} className="rounded-lg border border-glass bg-glass-2 p-3">
                  <p className="text-sm font-medium text-glass-text">{row.kolName}</p>
                  <p className="text-xs text-glass-text-3">{row.paymentCount} payments</p>
                  <p className="text-sm font-semibold text-accent2 mt-1">RM {row.totalAmount.toLocaleString()}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="glass-card p-5">
          <h3 className="font-semibold text-glass-text mb-3">Top Campaigns</h3>
          {topJobs.length === 0 ? (
            <p className="text-sm text-glass-text-3">No data.</p>
          ) : (
            <div className="space-y-2">
              {topJobs.map((row) => (
                <div key={row.jobId} className="rounded-lg border border-glass bg-glass-2 p-3">
                  <p className="text-sm font-medium text-glass-text truncate">{row.jobTitle}</p>
                  <p className="text-xs text-glass-text-3">{row.paymentCount} payments</p>
                  <p className="text-sm font-semibold text-accent2 mt-1">RM {row.totalAmount.toLocaleString()}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="glass-card p-5">
        <h3 className="font-semibold text-glass-text mb-3">By Agency</h3>
        {byAgency.length === 0 ? (
          <p className="text-sm text-glass-text-3">No agency data.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-glass text-glass-text-3 uppercase tracking-[0.05em] text-xs">
                  <th className="py-2 text-left">Agency</th>
                  <th className="py-2 text-left">Payments</th>
                  <th className="py-2 text-left">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-glass">
                {byAgency.map((row) => (
                  <tr key={row.agencyOrgId || 'unassigned'}>
                    <td className="py-2 text-glass-text">{row.agencyName}</td>
                    <td className="py-2 text-glass-text-2">{row.paymentCount}</td>
                    <td className="py-2 font-medium text-accent2">RM {row.totalAmount.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="glass-card p-5 space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <input
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
              setExpandedId(null);
            }}
            placeholder="Search campaign, KOL, or email"
            className="min-w-[260px] flex-1 rounded-lg border border-glass bg-glass-2 px-3 py-2 text-sm text-glass-text placeholder:text-glass-text-3"
          />
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value as '' | PaymentStatus);
              setPage(1);
              setExpandedId(null);
            }}
            className="w-[180px] rounded-lg border border-glass bg-glass-2 px-3 py-2 text-sm text-glass-text"
          >
            {statusOptions.map((opt) => (
              <option key={opt.label} value={opt.value} style={nativeOptionStyle}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-glass text-glass-text-3 uppercase tracking-[0.05em] text-xs">
                <th className="py-2 text-left w-10"></th>
                <th className="py-2 text-left">Campaign</th>
                <th className="py-2 text-left">KOL</th>
                <th className="py-2 text-left">Agency</th>
                <th className="py-2 text-left">Amount</th>
                <th className="py-2 text-left">Status</th>
                <th className="py-2 text-left">Created</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-glass">
              {loading ? (
                <tr>
                  <td colSpan={7} className="py-8 text-center text-glass-text-3">
                    Loading payments...
                  </td>
                </tr>
              ) : paymentItems.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-8 text-center text-glass-text-3">
                    No payments found.
                  </td>
                </tr>
              ) : (
                paymentItems.map((item) => {
                  const isExpanded = expandedId === item.payment.id;
                  return (
                    <React.Fragment key={item.payment.id}>
                      <tr className="hover:bg-glass-2">
                        <td className="py-2 pr-2">
                          <button
                            type="button"
                            className="p-1 rounded hover:bg-glass-3 text-glass-text-2"
                            onClick={() =>
                              setExpandedId((prev) => (prev === item.payment.id ? null : item.payment.id))
                            }
                          >
                            {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                          </button>
                        </td>
                        <td className="py-2 text-glass-text">{item.job.title}</td>
                        <td className="py-2">
                          <div className="text-glass-text">{item.kolProfile.name}</div>
                          <div className="text-xs text-glass-text-3">{item.kolProfile.email || 'No email'}</div>
                        </td>
                        <td className="py-2 text-glass-text-2">{item.job.managedByOrgName}</td>
                        <td className="py-2 font-medium text-accent2">RM {item.payment.amountRM.toLocaleString()}</td>
                        <td className="py-2">
                          <StatusBadge status={item.payment.status} />
                        </td>
                        <td className="py-2 text-glass-text-2">{new Date(item.payment.createdAt).toLocaleDateString()}</td>
                      </tr>

                      {isExpanded && (
                        <tr className="bg-glass-2/60">
                          <td></td>
                          <td colSpan={6} className="py-3">
                            <div className="grid lg:grid-cols-2 gap-3">
                              <div className="rounded-lg border border-glass bg-glass-1 p-3">
                                <p className="text-xs text-glass-text-3 uppercase tracking-[0.05em]">Payment detail</p>
                                <p className="text-sm text-glass-text mt-1">
                                  Description: {item.payment.description?.trim() || 'No description'}
                                </p>
                                <p className="text-sm text-glass-text mt-1">
                                  Paid at:{' '}
                                  {item.payment.paidAt ? new Date(item.payment.paidAt).toLocaleDateString() : 'Not paid yet'}
                                </p>
                                <p className="text-sm text-glass-text mt-1">Brand: {item.job.brandOrgName}</p>
                              </div>

                              <div className="rounded-lg border border-glass bg-glass-1 p-3">
                                <p className="text-xs text-glass-text-3 uppercase tracking-[0.05em]">Invitation layer</p>
                                {item.invitation ? (
                                  <>
                                    <p className="text-sm text-glass-text mt-1">Status: {item.invitation.status}</p>
                                    <p className="text-sm text-glass-text mt-1">
                                      Shortlisted: {item.invitation.isShortlisted ? 'Yes' : 'No'}
                                    </p>
                                    <p className="text-sm text-glass-text mt-1">
                                      Production approved: {item.invitation.isApprovedForProduction ? 'Yes' : 'No'}
                                    </p>
                                  </>
                                ) : (
                                  <p className="text-sm text-glass-text-2 mt-1">No invitation linked to this payment.</p>
                                )}
                              </div>

                              <div className="rounded-lg border border-glass bg-glass-1 p-3">
                                <p className="text-xs text-glass-text-3 uppercase tracking-[0.05em]">KOL work - Scripts</p>
                                <p className="text-sm text-glass-text mt-1">{item.kolWork.scripts.total} total</p>
                                <p className="text-xs text-glass-text-2 mt-1">{formatStatusMap(item.kolWork.scripts.byStatus)}</p>
                              </div>

                              <div className="rounded-lg border border-glass bg-glass-1 p-3">
                                <p className="text-xs text-glass-text-3 uppercase tracking-[0.05em]">KOL work - Submissions</p>
                                <p className="text-sm text-glass-text mt-1">{item.kolWork.submissions.total} total</p>
                                <p className="text-xs text-glass-text-2 mt-1">{formatStatusMap(item.kolWork.submissions.byStatus)}</p>
                              </div>

                              <div className="rounded-lg border border-glass bg-glass-1 p-3 lg:col-span-2">
                                <p className="text-xs text-glass-text-3 uppercase tracking-[0.05em]">KOL work - Contracts</p>
                                <p className="text-sm text-glass-text mt-1">
                                  {item.kolWork.contracts.total} total | {item.kolWork.contracts.acknowledged} acknowledged |{' '}
                                  {item.kolWork.contracts.pending} pending
                                </p>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between gap-2">
          <p className="text-sm text-glass-text-2">
            Page {pagination.page} of {pagination.totalPages} ({pagination.total} payments)
          </p>
          <div className="flex items-center gap-2">
            <Button type="button" variant="outline" size="sm" disabled={!canPrev} onClick={() => setPage((p) => Math.max(1, p - 1))}>
              Previous
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={!canNext}
              onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
            >
              Next
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentsAnalytics;
